import { useMemo, useState } from "react";
import {
  IncidentTableData,
  IncidentTableSortKey,
  IncidentTableSortState,
  UseIncidentTableSortParams,
} from "../../types";

const initialSortState: IncidentTableSortState = {
  key: "scannedAt",
  direction: "desc",
};

const getSortableValue = (
  row: IncidentTableData,
  key: IncidentTableSortKey,
): number | string => {
  const value = row[key];
  return value === undefined ? Number.NEGATIVE_INFINITY : value;
};

const sortIncidentRows = (
  rows: Array<IncidentTableData>,
  sortState: IncidentTableSortState,
): Array<IncidentTableData> => {
  return [...rows].sort((firstRow, secondRow) => {
    const firstValue = getSortableValue(firstRow, sortState.key);
    const secondValue = getSortableValue(secondRow, sortState.key);
    const comparison =
      firstValue > secondValue ? 1 : firstValue < secondValue ? -1 : 0;

    return sortState.direction === "asc" ? comparison : -comparison;
  });
};

export function useIncidentTableSort({ rows }: UseIncidentTableSortParams) {
  const [sortState, setSortState] =
    useState<IncidentTableSortState>(initialSortState);

  const sortedRows = useMemo(() => {
    return sortIncidentRows(rows, sortState);
  }, [rows, sortState]);

  const updateSort = (key: IncidentTableSortKey): void => {
    setSortState((currentSortState) => ({
      key,
      direction:
        currentSortState.key === key && currentSortState.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  return {
    sortedRows,
    sortState,
    updateSort,
  };
}
